import { StrictMode } from 'react';
import { logger } from './utils/logger';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import { AuthProvider } from './contexts/AuthContext';
import { StrategyProvider } from './contexts/StrategyContext';
import { MarketDataProvider } from './contexts/MarketDataContext';
import ErrorOverlay from './components/ErrorOverlay';
import { errorReporter, enableErrorDebugging } from './utils/errorHandlers';
import { installStaleChunkAutoReload } from './utils/lazyWithReload';
import { initSentry } from './utils/sentry';
import { initAnalytics } from './utils/analytics';
import './index.css';

// Initialize error handling as early as possible
logger.debug('🚀 Initializing Trading Strategy Builder...');

// Initialize global error handling before React renders
errorReporter.init();
enableErrorDebugging();
// Self-heal stale-chunk preload failures after a redeploy (cooldown-guarded
// reload; see lazyWithReload.ts for the route-level counterpart).
installStaleChunkAutoReload();

// M-C — observability. Both no-op when their env vars are unset.
// Sentry MUST initialise before React renders so synchronous boot errors
// are captured. Analytics can wait until interactive but the init cost is
// negligible so we just run it here too.
initSentry();
initAnalytics();

// O13 — bake the per-build timestamp into the ENTRY bundle so every deploy
// produces a UNIQUE entry-chunk hash. Why this matters: during a deploy
// propagation race the Pages SPA fallback can answer a hashed-chunk request
// with index.html (HTML, 200); a browser that caches that poisoned response
// under the .js URL then white-screens on every reload. Because the next
// deploy's fresh index.html now points at a DIFFERENT hash the browser never
// cached, the stale entry can't poison it — the failure mode self-heals on
// any redeploy. Doubles as a support build-stamp on window.
declare const __BUILD_TIME__: string;
const buildStamp = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : 'dev';
(window as unknown as { __TRADEATORY_BUILD__?: string }).__TRADEATORY_BUILD__ = buildStamp;
logger.debug(`🏷️ Build ${buildStamp}`);

// Additional global error handling for module loading errors
window.addEventListener('load', () => {
  logger.debug('✅ App loaded successfully');
});

// Handle unhandled promise rejections at the module level
window.addEventListener('unhandledrejection', (event) => {
  console.error('🚨 Unhandled Promise Rejection:', event.reason);
});

// Create root and render the application
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const root = createRoot(rootElement);

// Enhanced error boundary wrapper for the entire app
const AppWithErrorHandling = () => {
  return (
    <>
      {/* Main application */}
      <StrictMode>
        <AuthProvider>
          <StrategyProvider>
            <MarketDataProvider>
              <App />
            </MarketDataProvider>
          </StrategyProvider>
        </AuthProvider>
      </StrictMode>
      
      {/* Error overlay - rendered outside StrictMode to catch StrictMode errors */}
      <ErrorOverlay />
      
      {/* Development utilities */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-4 right-4 z-50 pointer-events-none">
          <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-auto">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>DEV MODE</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Render with additional safety
try {
  root.render(<AppWithErrorHandling />);
  logger.debug('✅ React app rendered successfully');
} catch (error) {
  console.error('🚨 Failed to render React app:', error);
  
  // Fallback rendering if React fails completely
  if (rootElement) {
    rootElement.innerHTML = `
      <div style="
        display: flex; 
        items: center; 
        justify-content: center; 
        min-height: 100vh; 
        background-color: #fef2f2; 
        font-family: system-ui, -apple-system, sans-serif;
        padding: 2rem;
      ">
        <div style="
          background: white; 
          padding: 2rem; 
          border-radius: 0.5rem; 
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); 
          max-width: 32rem; 
          text-align: center;
        ">
          <div style="color: #dc2626; margin-bottom: 1rem;">
            <svg style="width: 3rem; height: 3rem; margin: 0 auto;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h1 style="font-size: 1.5rem; font-weight: bold; color: #111827; margin-bottom: 1rem;">
            Application Failed to Load
          </h1>
          <p style="color: #6b7280; margin-bottom: 1.5rem;">
            The Trading Strategy Builder encountered a critical error and cannot start. Please refresh the page or contact support.
          </p>
          <button 
            onclick="window.location.reload()" 
            style="
              background-color: #3b82f6; 
              color: white; 
              padding: 0.5rem 1rem; 
              border: none; 
              border-radius: 0.375rem; 
              cursor: pointer; 
              font-weight: 500;
            "
          >
            Refresh Page
          </button>
          ${process.env.NODE_ENV === 'development' ? `
            <details style="margin-top: 1rem; text-align: left;">
              <summary style="cursor: pointer; color: #6b7280; font-size: 0.875rem;">Error Details</summary>
              <pre style="
                background-color: #f3f4f6; 
                padding: 1rem; 
                border-radius: 0.375rem; 
                margin-top: 0.5rem; 
                font-size: 0.75rem; 
                color: #374151; 
                overflow: auto;
                white-space: pre-wrap;
              ">${String(error).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            </details>
          ` : ''}
        </div>
      </div>
    `;
  }
}

// Performance monitoring
if ('performance' in window && 'measure' in window.performance) {
  window.performance.mark('app-render-complete');
  
  // Log performance metrics after the app loads
  setTimeout(() => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      logger.debug('📊 Performance Metrics:');
      logger.debug(`  DOM Content Loaded: ${navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart}ms`);
      logger.debug(`  Page Load: ${navigation.loadEventEnd - navigation.loadEventStart}ms`);
      logger.debug(`  Total Load Time: ${navigation.loadEventEnd - navigation.fetchStart}ms`);
    }
  }, 100);
}

// Export for debugging
if (process.env.NODE_ENV === 'development') {
  (window as any).__APP_ROOT__ = root;
  (window as any).__RELOAD_APP__ = () => {
    logger.debug('🔄 Reloading app...');
    window.location.reload();
  };
}

// import { StrictMode } from 'react';
// import { createRoot } from 'react-dom/client';
// import App from './App.tsx';
// import { AuthProvider } from './contexts/AuthContext';
// import './index.css';

// createRoot(document.getElementById('root')!).render(
//   <StrictMode>
//     <AuthProvider>
//       <App />
//     </AuthProvider>
//   </StrictMode>
// );